import {
  Typography,
  Grid,
  AppButton,
  Input,
  AppSelect,
  FormField,
} from "@/components/app";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  submitContactUs,
  resolveContactUsErrorMessage,
  type ContactUsInquiryType,
} from "@/api";
import { Mail, Phone, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { useState } from "react";

const inquiryTypes = [
  { label: "General Inquiry", value: "general" },
  { label: "Order Related", value: "order" },
  { label: "Customization Request", value: "customization" },
  { label: "Technical Support", value: "support" },
  { label: "Feedback", value: "feedback" },
];

const emptyForm = {
  fullName: "",
  email: "",
  phone: "",
  inquiryType: "general",
  message: "",
};

export default function ContactUsPage() {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      await submitContactUs({
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        inquiryType: formData.inquiryType as ContactUsInquiryType,
        message: formData.message.trim(),
      });
      setFormData(emptyForm);
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(resolveContactUsErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(false);
  };

  return (
    <main className="pb-10 lg:pb-16 xl:pb-[100px]">
      <div className="wrapper">
        <Grid container spacing={5}>
          {/* Left Section - Image */}
          <Grid item xs={12} md={6} className="hidden md:block">
            <div className="h-full lg:rounded-[30px] rounded-2xl md:bg-[url('/images/login-img.webp')] bg-no-repeat bg-cover bg-top bg-transparent relative">
              <img
                src="/images/login-img.webp"
                alt="Contact Us"
                className="w-full object-cover h-full md:hidden"
              />
              <div className="absolute bottom-5 left-5 right-5 bg-[#f9f6f7]/50 border border-border-light backdrop-blur-sm px-6 py-5 flex flex-wrap  gap-2 items-start md:items-center justify-around rounded-full">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <Typography variant="body2" className="text-foreground">
                    info@smorajewellery.com
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-primary" />
                  <Typography variant="body2" className="text-foreground">
                    +91 70692 57283
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <Typography variant="body2" className="text-foreground">
                    Mendarda, Gujarat, India
                  </Typography>
                </div>
              </div>
            </div>
          </Grid>

          {/* Right Section - Form */}
          <Grid item xs={12} md={6}>
            <div
              className="lg:rounded-[30px] rounded-2xl bg-[#F3EAE4] h-full border border-border"
              style={{ boxShadow: "8px 8px 44px 0px #00000012" }}
            >
              <div className="xl:p-8 p-4 md:p-6">
                <div className="mb-8">
                  <Typography variant="h3" className="mb-3">
                    Get in Touch
                  </Typography>
                  <Typography variant="body1">
                    Have questions about our jewellery, orders, or
                    customization? Our team is here to help.
                  </Typography>
                </div>

                <form onSubmit={handleSubmit} aria-busy={isSubmitting}>
                  <div className="lg:space-y-5 space-y-3">
                    {submitSuccess && (
                      <Alert
                        role="status"
                        className="border-emerald-200/90 bg-emerald-50/95 text-emerald-950 [&>svg]:text-emerald-700"
                      >
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        <AlertTitle>Message sent</AlertTitle>
                        <AlertDescription>
                          Thank you — we&apos;ll get back to you soon.
                        </AlertDescription>
                      </Alert>
                    )}

                    {submitError && (
                      <Alert variant="destructive" role="alert">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <AlertTitle>Couldn&apos;t send your message</AlertTitle>
                        <AlertDescription>{submitError}</AlertDescription>
                      </Alert>
                    )}

                    <div>
                      <FormField label="Full Name" htmlFor="fullName">
                        <Input
                          id="fullName"
                          size="lg"
                          placeholder="Enter your full name"
                          value={formData.fullName}
                          onChange={(e) =>
                            handleChange("fullName", e.target.value)
                          }
                          required
                          disabled={isSubmitting}
                          autoComplete="name"
                        />
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Email Address" htmlFor="email">
                        <Input
                          id="email"
                          type="email"
                          size="lg"
                          placeholder="Enter your email address"
                          value={formData.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          required
                          disabled={isSubmitting}
                          autoComplete="email"
                        />
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Phone Number" htmlFor="phone">
                        <Input
                          id="phone"
                          type="tel"
                          size="lg"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={(e) =>
                            handleChange("phone", e.target.value)
                          }
                          required
                          disabled={isSubmitting}
                          autoComplete="tel"
                        />
                      </FormField>
                    </div>

                    <div>
                      <FormField
                        label="Inquiry Type (Dropdown)"
                        htmlFor="inquiryType"
                      >
                        <AppSelect
                          value={formData.inquiryType}
                          onValueChange={(value) =>
                            handleChange("inquiryType", value)
                          }
                          options={inquiryTypes}
                          size="lg"
                          disabled={isSubmitting}
                        />
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Message" htmlFor="message">
                        <textarea
                          id="message"
                          placeholder="Tell us how we can help you..."
                          value={formData.message}
                          onChange={(e) =>
                            handleChange("message", e.target.value)
                          }
                          rows={5}
                          required
                          disabled={isSubmitting}
                          className="w-full px-[14px] py-3 rounded-[12px] border border-border-light bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 resize-none text-base disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </FormField>
                    </div>

                    <div>
                      <AppButton
                        type="submit"
                        fullWidth
                        size="lg"
                        loading={isSubmitting}
                      >
                        {isSubmitting ? "Sending…" : "Get Early Access"}
                      </AppButton>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </main>
  );
}
